import { AxiosError } from 'axios';
import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';
import type { AppliedModel } from './scenarioIntelligence';
import { env } from '@/lib/env';
import { sessionHelper } from '@/auth/sessionHelper';

// ─────────────────────────────────────────────────────────────────────────────
// Scenario API typings — mirrors the DTOs in
// RestApi.Tenant/Controllers/AIInfo/ScenariosController.cs.
//
// The summary shape is wide on purpose: every field that powers the
// scenario library's filter bar comes pre-computed from the server so the
// UI can pick / sort without a second round-trip. New filter dimensions
// land here first, then in ScenarioFilterBar / ScenariosStore.
// ─────────────────────────────────────────────────────────────────────────────

export interface ScenarioSummary {
  scenarioId: string;
  agentId: number;
  agentCanonicalName: string;
  /** Human-readable agent label, mirrored from ai.AiAgent.DisplayName at sync time. */
  agentDisplayName?: string;
  /** Vertical-neutral free string: "Patient", "Provider", "Customer", … */
  audience?: string;
  areaSlug: string;
  description?: string;
  /** Taxonomy code; "U" for uncategorized. */
  category: string;
  /** Human label for the code, sourced from aivalidation.ScenarioCategory. */
  categoryHint: string;
  turnCount: number;
  isMultiTurn: boolean;
  hasSkillAssertions: boolean;
  hasToolCallExpectations: boolean;
  hasConversationLevelRules: boolean;
  hasBannedPhrases: boolean;
  assertionCount: number;
  /** Comma-separated tag string from the scenario JSON; null when no tags. */
  tags?: string | null;
  filePath: string;
  fileName: string;

  // ─── Legacy convenience flags (derived) ────────────────────────────────
  // The new filter bar uses category code + hasBannedPhrases / hasSkillAssertions
  // directly. These older booleans are kept on the wire for backward compat
  // with the existing pill rendering in ScenarioDetailDrawer / ScenarioLibraryPage.
  // Remove once those views adopt the category-based pills.
  /** @deprecated derived from category === "A" or filename contains "safety". */
  isSafety?: boolean;
  /** @deprecated derived from category === "G". */
  isAdversarial?: boolean;
}

// Subset of the parsed ScenarioDefinition we render in the drawer.
export interface ScenarioTurnSpec {
  user?: string;
  expect?: ScenarioTurnExpectation | null;
}

export interface ScenarioTurnExpectation {
  skillsLoaded?: string[];
  skillsNotLoaded?: string[];
  responseMustContain?: string[];
  responseMustContainAnyOf?: string[];
  responseMustNotContain?: string[];
  severityScreenComplete?: boolean;
}

export interface ScenarioConversationAssertion {
  rule?: string;
  mustNotAppearInAnyTurn?: string[];
  mustAppearAtLeastOnce?: string[];
  requireNumbersGroundedInToolOutput?: boolean | null;
  groundingTolerance?: number | null;
  groundingIgnorePatterns?: string[];
}

export interface ScenarioParsed {
  scenarioId: string;
  agentId: number;
  merchantId: number;
  memberId: number;
  description?: string;
  turns?: ScenarioTurnSpec[];
  conversationLevelAssertions?: ScenarioConversationAssertion[];
  /** The protocol TEMPLATE a template agent's scenario runs against; 0 for patient scenarios. */
  infusionProtocolId?: number;
  /** A family member signed in for the patient; 0 = the patient themself. */
  memberContactId?: number;
}

export interface ScenarioDetail {
  summary: ScenarioSummary;
  rawJson: string;
  parsed: ScenarioParsed;
  /**
   * The hash of the version this was read at. The form editor sends it back with a save, which the server refuses as a
   * conflict when someone saved since (studio-foundation review, 4 Nov 2026). Absent from older APIs.
   */
  baseHash?: string | null;
  /** The stored lifecycle stage, so an edit keeps it unless the person changes it. Absent from older APIs. */
  lifecycleStage?: string | null;
}

export interface ScenarioRunHistoryItem {
  validationRunId: number;
  resultId: number;
  runDate: string;
  passed: boolean;
  score: number;
  turnCount: number;
  failedAssertionCount: number;
  environment?: string;
  triggerSource?: string;
}

/**
 * One row of the vertical-agnostic category taxonomy.
 * Sourced from aivalidation.ScenarioCategory; populates the filter bar's
 * "Category" dropdown so labels stay in sync with whatever vertical
 * (oncology, RPM, finance, …) the merchant has configured.
 */
export interface ScenarioCategoryDto {
  domain: string;
  code: string;
  displayName: string;
  description?: string | null;
  displayOrder: number;
}

/**
 * Server-side filter shape sent to /api/Scenarios/Search. Every field is
 * optional; omitting them all returns the full active scenario set.
 */
export interface ScenarioSearchParams {
  agentId?: number;
  domain?: string;
  /** Comma-separated category codes — e.g. "A,G,L". */
  categories?: string;
  /** "multi" | "single" | undefined (= any). */
  turnCount?: 'multi' | 'single';
  audience?: string;
  /** Comma-separated tag substrings; ANY-of match. */
  tags?: string;
  hasSkillAssertions?: boolean;
  hasBannedPhrases?: boolean;
}

function buildQuery(params: ScenarioSearchParams): Record<string, string | number | boolean> {
  const q: Record<string, string | number | boolean> = {};
  if (params.agentId != null) q.agentId = params.agentId;
  if (params.domain) q.domain = params.domain;
  if (params.categories) q.categories = params.categories;
  if (params.turnCount) q.turnCount = params.turnCount;
  if (params.audience) q.audience = params.audience;
  if (params.tags) q.tags = params.tags;
  if (params.hasSkillAssertions != null) q.hasSkillAssertions = params.hasSkillAssertions;
  if (params.hasBannedPhrases != null) q.hasBannedPhrases = params.hasBannedPhrases;
  return q;
}

export async function searchScenarios(params: ScenarioSearchParams = {}): Promise<ScenarioSummary[]> {
  const wrapped = await apiGet<OperationResult<ScenarioSummary[]>>(
    '/api/Scenarios/Search',
    buildQuery(params),
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load scenarios');
  return wrapped.data ?? [];
}

export async function getScenario(filePath: string): Promise<ScenarioDetail> {
  const wrapped = await apiGet<OperationResult<ScenarioDetail>>('/api/Scenarios/Get', { filePath });
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load scenario');
  }
  return wrapped.data;
}

export async function getScenarioRunHistory(
  scenarioId: string,
  agentId?: number,
  limit = 30
): Promise<ScenarioRunHistoryItem[]> {
  const wrapped = await apiGet<OperationResult<ScenarioRunHistoryItem[]>>(
    '/api/Scenarios/RunHistory',
    { scenarioId, ...(agentId ? { agentId } : {}), limit }
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load run history');
  return wrapped.data ?? [];
}

/**
 * Fetch the active category taxonomy. Optionally filtered to one domain
 * (oncology, rpm, …). Used by ScenarioFilterBar to populate the "Category"
 * dropdown — the labels you see depend entirely on what's configured in
 * the database, NOT on hard-coded UI text.
 */
export async function listScenarioCategories(domain?: string): Promise<ScenarioCategoryDto[]> {
  const wrapped = await apiGet<OperationResult<ScenarioCategoryDto[]>>(
    '/api/Scenarios/Categories',
    domain ? { domain } : undefined,
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load scenario categories');
  return wrapped.data ?? [];
}

// ─────────────────────────────────────────────────────────────────────────────
// Cloud-native authoring surface — DB-first scenario writes + AI co-pilot
// ─────────────────────────────────────────────────────────────────────────────

export interface UpsertScenarioRequest {
  domain: string;
  scenarioId: string;
  agentId: number;
  merchantId: number;
  memberId: number;
  description?: string;
  turns: ScenarioTurnSpec[];
  conversationLevelAssertions?: ScenarioConversationAssertion[];
  /** Draft | Smoke | Alpha | Beta | Production. Omitted keeps the scenario's stage; a new scenario starts at Alpha. */
  lifecycleStage?: string;
  /** The version the editor opened (ScenarioDetail.baseHash). Omitted = create a new scenario, refused when the id exists. */
  baseHash?: string | null;
  /** The category code the person chose; omitted or "U" = none chosen. */
  category?: string | null;
  infusionProtocolId?: number;
  memberContactId?: number;
  /**
   * The person confirmed removing a turn or rule that held checks the editor does not show. Without it the server
   * refuses a save that would lose any of them (ScenarioHiddenChecks).
   */
  acceptRemovedChecks?: boolean;
}

export type UpsertScenarioOutcome = 'Inserted' | 'Updated' | 'Unchanged' | 'Conflict' | 'AlreadyExists' | 'NotFound' | 'Refused';

export interface UpsertScenarioResponse {
  scenarioId: string;
  indexId: number;
  snapshotId: number;
  outcome: UpsertScenarioOutcome;
  /** Why nothing was saved, in words; empty when it was. */
  problems: string[];
  /** Saved: the new version's hash. Conflict: the hash of the version someone else saved. */
  hash?: string | null;
  /** Conflict: who saved the newer version, and when. */
  currentSavedBy?: number;
  currentSavedAtUtc?: string | null;
}

/** Whether a save stored the scenario (or found nothing to change). */
export function upsertSaved(r: UpsertScenarioResponse): boolean {
  return r.outcome === 'Inserted' || r.outcome === 'Updated' || r.outcome === 'Unchanged';
}

/**
 * Saves the form editor's scenario. Never throws for an answer the server gave: a conflict (HTTP 409), an id that
 * already exists, a refusal and a not-found come back as outcomes for the drawer to show, as the advanced editor's save
 * does. A transport failure still throws.
 */
export async function upsertScenario(req: UpsertScenarioRequest): Promise<UpsertScenarioResponse> {
  let wrapped: OperationResult<UpsertScenarioResponse>;
  try {
    wrapped = await apiPost<OperationResult<UpsertScenarioResponse>>('/api/Scenarios/Upsert', req);
  } catch (e) {
    const body = (e as AxiosError<OperationResult<UpsertScenarioResponse>>).response?.data;
    if (!body?.data?.outcome) throw e;
    wrapped = body;
  }
  if (!wrapped.data?.outcome) throw new Error(wrapped.message || 'Failed to save scenario');
  return { ...wrapped.data, problems: wrapped.data.problems ?? (wrapped.isSuccess ? [] : [wrapped.message ?? 'Not saved.']) };
}

/** A scenario's tags, stored comma-separated on its index row. */
export function scenarioTags(s: Pick<ScenarioSummary, 'tags'>): string[] {
  return (s.tags ?? '').split(',').map((t) => t.trim()).filter(Boolean);
}

/**
 * Save ONLY a scenario's tags — never its body, so a tag edit cannot drop an assertion the editor does not show
 * (POST /api/Scenarios/Tags). Replaces the whole set; returns the tags as stored (lower case, hyphenated, each once).
 */
export async function setScenarioTags(scenarioId: string, tags: string[]): Promise<string[]> {
  const wrapped = await apiPost<OperationResult<string[]>>('/api/Scenarios/Tags', { scenarioId, tags });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to save tags');
  return wrapped.data ?? [];
}

export interface DraftScenarioRequest {
  agentId: number;
  userRequest: string;
  focusCategory?: string;
  /** D-SI-9: the ai.AIModel row to draft with, for this draft only. Omitted = the plan's Scenario model. */
  scenarioModelId?: number | null;
}

export interface DraftScenarioResponse {
  draft: ScenarioParsed | null;
  rationale?: string;
  similarScenarioIds: string[];
  /** D-SI-9: the model that drafted it. */
  answeredBy?: AppliedModel | null;
}

export async function draftScenario(req: DraftScenarioRequest): Promise<DraftScenarioResponse> {
  const wrapped = await apiPost<OperationResult<DraftScenarioResponse>>('/api/Scenarios/Draft', req);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'AI drafting failed');
  }
  return wrapped.data;
}

export interface SimilarScenarioRow {
  scenarioId: string;
  description?: string;
  category: string;
  /** 0..1 Jaccard score. */
  score: number;
}

export async function getSimilarScenarios(
  agentId: number,
  text: string,
  excludeScenarioId?: string,
  limit = 5,
): Promise<SimilarScenarioRow[]> {
  const wrapped = await apiGet<OperationResult<SimilarScenarioRow[]>>('/api/Scenarios/Similar', {
    agentId,
    text,
    ...(excludeScenarioId ? { excludeScenarioId } : {}),
    limit,
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load similar scenarios');
  return wrapped.data ?? [];
}

export interface CoverageGapRow {
  code: string;
  label: string;
  count: number;
  /** True when the agent has fewer than 2 scenarios in this category. */
  isGap: boolean;
}

export async function getCoverageGaps(agentId: number, domain?: string): Promise<CoverageGapRow[]> {
  const wrapped = await apiGet<OperationResult<CoverageGapRow[]>>('/api/Scenarios/CoverageGaps', {
    agentId,
    ...(domain ? { domain } : {}),
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load coverage gaps');
  return wrapped.data ?? [];
}

/**
 * Trigger a download of the canonical JSON file for one scenario. Honors the
 * cloud-native pattern: DB is editorial truth, this is the optional disk path.
 *
 * Uses fetch + blob (rather than a plain anchor href) so the existing axios
 * Bearer-token auth header is reused. The downloaded file would round-trip
 * cleanly through ScenarioFileSyncManager if a developer chose to commit it.
 */
export async function exportScenarioJson(scenarioId: string): Promise<void> {
  const url = `${env.apiUrl}/api/Scenarios/Export/${encodeURIComponent(scenarioId)}`;
  const token = sessionHelper.getToken();
  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = `${scenarioId}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

/** Fetch one scenario by scenarioId (preferred over the legacy filePath form). */
export async function getScenarioById(scenarioId: string): Promise<ScenarioDetail> {
  const wrapped = await apiGet<OperationResult<ScenarioDetail>>('/api/Scenarios/Get', { scenarioId });
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load scenario');
  }
  return wrapped.data;
}
